import React, { lazy, useMemo, useState } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { ApplyFilterIcon, CrossIcon, ResetIcon } from "../Icons";
import SecondaryBtn from "../../Buttons/SecondaryBtn";
import PrimaryBtn from "../../Buttons/PrimaryBtn";
import CustomFileTypeSelectDropdown from "./CustomFileTypeSelectDropdown";
import MyDateRangePicker from "./MyDateRangePicker";
import { studentDashboard_AI_chatbot_image } from "../../../assets/images/images";
import RadioGroup from "@mui/material/RadioGroup";
import { useRepoQuery } from "../hooks/useRepoQuery";
import { useDebounce } from "../hooks/useDebounce";
import { useAuth } from "../../../features/auth/AuthContext";
import { useSelector } from "react-redux";
const SwipeableDrawer = lazy(() => import("@mui/material/SwipeableDrawer"));

const Filter = ({
  open,
  close,
  applyFilter,
  onReset,
  fileType = false,
  updatedOn = false,
  activityActions = false,
  invitedBy = false,
  sortedInvitations = [],
  showUploadType = false,
  filterAPI = "",
  view = null,
  enableResults,
  currentFolder = null,
}) => {
  const [selectedFileType, setSelectedFileType] = useState([]);
  const [selectedInvitedBy, setSelectedInvitedBy] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [actions, setActions] = useState([]);
  const [uploadType, setUploadType] = useState("");
  const [range, setRange] = useState([
    { startDate: undefined, endDate: undefined, key: "selection" },
  ]);
  const [openComponent, setOpenComponent] = useState(false);
  const globalState = useSelector(state => state.globalState)
  const isMarketPlace = globalState?.enabled_modules?.includes('marketplace');


  const { token } = useAuth();

  const previewFilters = useMemo(
    () => ({
      start_date: range?.[0]?.startDate,
      end_date: range?.[0]?.endDate,
      file_type: selectedFileType,
      actions,
      upload_type: uploadType,
    }),
    [
      range,
      selectedFileType,
      selectedInvitedBy,
      selectedStatus,
      actions,
      uploadType,
    ],
  );
  const debouncedFilters = useDebounce(previewFilters, 400);

  const shouldFetchPreview = open && (Boolean(currentFolder) || enableResults)

  const { data: previewData, isLoading: previewLoading } = useRepoQuery({
    token,
    endpoint: filterAPI,
    queryKey: ["filterPreview", debouncedFilters],
    folderId: currentFolder?.id,
    filters: debouncedFilters,
    enabled: shouldFetchPreview,
    ...(view && { view }),
  });

  const statusOptions = isMarketPlace ? [{value: "Enrolled", label: "Enrolled"}, { value: "Purchased", label: "Purchased" },{ value: "Invited & Enrolled", label: "Invited & Enrolled" }  ] : [
    { value: "Enrolled", label: "Enrolled" },
    { value: "Invited", label: "Invited" },
    { value: "Invited & Enrolled", label: "Invited & Enrolled" },
  ];

  const invitedByOptions = React.useMemo(() => {
    const map = new Map();
    (sortedInvitations || []).forEach((inv) => {
      if (inv.invited_by && !map.has(inv.invited_by)) {
        map.set(inv.invited_by, {
          value: inv.invited_by,
          label: inv.invited_by,
          image: inv.teacher_profile_pic || studentDashboard_AI_chatbot_image,
        });
      }
    });
    return [...map.values()].sort((a, b) =>
      (a.label || "").localeCompare(b.label || ""),
    );
  }, [sortedInvitations]);
  // const [updatedDate, setUpdatedDate] = useState();

  const toggleComponent = (component = null) => {
    setOpenComponent((prev) => (prev === component ? null : component));
  };

  const handleDateChange = (value) => {
    setRange(value);
  };

  const handleDateClear = () => {
    setRange([{ startDate: undefined, endDate: undefined, key: "selection" }]);
  };

  const actionOptions = [
    { value: "create_folder", label: "Create Folder" },
    { value: "upload_file", label: "Upload File" },
    { value: "upload_folder", label: "Upload Folder" },
    { value: "move_file", label: "Move File" },
    { value: "move_folder", label: "Move Folder" },
    { value: "delete_file", label: "Delete File" },
    { value: "delete_folder", label: "Delete Folder" },
    { value: "permanent_delete_file", label: "Permanently Delete File" },
    { value: "permanent_delete_folder", label: "Permanently Delete Folder" },
    { value: "restore_file", label: "Restore File" },
    { value: "restore_folder", label: "Restore Folder" },
    { value: "rename_file", label: "Rename File" },
    { value: "rename_folder", label: "Rename Folder" },
    { value: "favorite_add_file", label: "Add File to Favorites" },
    { value: "favorite_add_folder", label: "Add Folder to Favorites" },
    { value: "favorite_remove_file", label: "Remove File from Favorites" },
    { value: "favorite_remove_folder", label: "Remove Folder from Favorites" },
    { value: "import_google_drive", label: "Import from Google Drive" },
  ];

  const uploadTypeOptions = [
    { label: "All", value: "" },
    { label: "Generated", value: "Generated" },
    { label: "Manual", value: "Manual" },
  ];

  const handleUploadTypeChange = (e) => {
    setUploadType(e?.target?.value || "");
  };

  return (
    <SwipeableDrawer
      open={open}
      onClose={close}
      anchor="right"
      PaperProps={{
        sx: {
          width: {
            xs: "70%",
            sm: 300,
            md: 360,
          },
        },
      }}
    >
      <div className="p-4 flex flex-col justify-between fixed inset-y-0 right-0 z-50 w-full sm:w-[450px]  sm:rounded-tl-2xl bg-white shadow-custom transition-transform duration-300 ease-in-out translate-x-0">
        <div className="flex flex-col gap-4 overflow-auto min-h-[100%]">
          <div className="flex justify-between items-center">
            <span className="text-[#232323] font-medium text-[1.1rem]">
              {" "}
              Filter
            </span>
            <button className="bg-transparent border-none" onClick={close}>
              <CrossIcon w="12" h="12" />
            </button>
          </div>
          <div className="custom-scrollbar flex-1 overflow-auto space-y-6 h-[76dvh] w-full pe-3 box-border">
            {fileType && (
              <div className="flex flex-col gap-2">
                <span className="text-[#232323] text-[0.9rem] font-medium">
                  File Type
                </span>
                {/* <CustomFileTypeSelectDropdown
                  selectedFileTypes={selectedFileType}
                  onChange={setSelectedFileType}
                  isOpen={openComponent === "fileType"}
                  toggleDropdown={() => toggleComponent("fileType")}
                /> */}
                <CustomFileTypeSelectDropdown
                  selectedFileTypes={selectedFileType}
                  onChange={setSelectedFileType}
                  isOpen={openComponent === "fileType"}
                  toggleDropdown={() => toggleComponent("fileType")}
                  // enableShowMore={true}
                />
              </div>
            )}
            {invitedBy && (
              <div className="flex flex-col gap-2">
                <span className="text-[#232323] text-[0.9rem] font-medium">
                  Invited By
                </span>
                <CustomFileTypeSelectDropdown
                  fileTypeOptions={invitedByOptions}
                  selectedFileTypes={selectedInvitedBy}
                  onChange={setSelectedInvitedBy}
                  isOpen={openComponent === "invitedBy"}
                  toggleDropdown={() => toggleComponent("invitedBy")}
                  placeholder="Select Invited By"
                />
              </div>
            )}
            {activityActions && (
              <div className="flex flex-col gap-2">
                <span className="text-[#232323] text-[0.9rem] font-medium">
                  Select Action
                </span>
                <CustomFileTypeSelectDropdown
                  fileTypeOptions={actionOptions}
                  selectedFileTypes={actions}
                  onChange={setActions}
                  isOpen={openComponent === "action"}
                  toggleDropdown={() => toggleComponent("action")}
                  placeholder="Select Action Type"
                  enableShowMore={true}
                  // visibleCount={4}
                />
              </div>
            )}
            {updatedOn && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between w-full">
                  <span className="text-[#232323] text-[0.9rem] font-medium">
                    Updated On
                  </span>
                  <button
                    onClick={handleDateClear}
                    className="bg-transparent border-none text-[#0068FF] text-[0.9rem] font-medium"
                  >
                    Clear
                  </button>
                </div>
                <MyDateRangePicker
                  range={range}
                  setRange={handleDateChange}
                  isOpen={openComponent === "dateRange"}
                  toggleComponent={() => toggleComponent("dateRange")}
                  onRangeSelect={handleDateChange}
                />
              </div>
            )}

            {invitedBy && (
              <div className="flex flex-col gap-2 pt-1">
                <span className="text-[#232323] text-[0.9rem] font-medium">
                  Status
                </span>
                {statusOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center gap-2 text-[#232323] text-[0.9rem]"
                  >
                    <input
                      type="checkbox"
                      id={`filter-status-${option.value}`}
                      checked={selectedStatus.includes(option.value)}
                      onChange={() => {
                        setSelectedStatus((prev) =>
                          prev.includes(option.value)
                            ? prev.filter((v) => v !== option.value)
                            : [...prev, option.value],
                        );
                      }}
                      className="w-4 h-4 rounded border border-[#232323] text-[#232323] focus:ring-0 focus:ring-offset-0 cursor-pointer shrink-0"
                    />
                    <span className="select-none">{option.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* {invitedBy && (
              <div className="flex flex-col gap-2 pt-1">
                <span className="text-[#232323] text-[0.9rem] font-medium">
                  Status
                </span>
                {statusOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 cursor-pointer text-[#232323] text-[0.9rem]"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatus.includes(option.value)}
                      onChange={() => {
                        setSelectedStatus((prev) =>
                          prev.includes(option.value)
                            ? prev.filter((v) => v !== option.value)
                            : [...prev, option.value],
                        );
                      }}
                      className="w-4 h-4 rounded border border-[#232323] text-[#232323] focus:ring-0 focus:ring-offset-0 cursor-pointer"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            )} */}

            {showUploadType && (
              <div className="flex flex-col gap-2">
                <span className="text-[#232323] text-[0.9rem] font-medium">
                  File Origin
                </span>
                <RadioGroup
                  sx={{ gap: 0 }}
                  value={uploadType}
                  onChange={handleUploadTypeChange}
                >
                  {uploadTypeOptions?.map((type) => (
                    <FormControlLabel
                      key={type?.value}
                      value={type?.value}
                      control={<Radio size="small" />}
                      label={type?.label || ""}
                      sx={{
                        margin: 0, // remove default margin
                        gap: 0, // space between radio & label
                        "& .MuiTypography-root": {
                          fontSize: "0.9rem", // 🔥 label font size
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {enableResults && (
              <div className="w-full text-right">
                <span className="text-[#555555] text-[0.9rem] font-normal">
                  Results {previewData?.count || 0}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between w-full">
              <SecondaryBtn
                label="Reset Filter"
                iconPosition="right"
                handleClick={() => {
                  setRange([
                    {
                      startDate: undefined,
                      endDate: undefined,
                      key: "selection",
                    },
                  ]);
                  setSelectedFileType([]);
                  setSelectedInvitedBy([]);
                  setSelectedStatus([]);
                  setUploadType("");
                  setActions([]);
                  onReset?.();
                }}
                icon={<ResetIcon />}
              />
              <PrimaryBtn
                label="Apply Filter"
                iconPosition="right"
                handleClick={() => {
                  applyFilter({
                    start_date: range?.[0]?.startDate,
                    end_date: range?.[0]?.endDate,
                    file_type: selectedFileType,
                    ...(invitedBy && {
                      invited_by: selectedInvitedBy,
                      status: selectedStatus,
                    }),
                    actions,
                    upload_type: uploadType
                  });
                  toggleComponent(null);
                }}
                icon={<ApplyFilterIcon />}
              />
            </div>
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
};

export default Filter;
